import type {
  CSSProperties,
  ForwardedRef,
  ReactElement,
  ReactNode,
  PointerEvent as ReactPointerEvent,
} from 'react'
import {
  createContext,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { resolveVerticalDropTarget } from '@/components/shared/drag-drop/vertical-drop-target'
/**
 * Consolidated DataTable component with modular structure
 *
 * Features:
 * - Column resizing with visual feedback at header level
 * - Sorting with visual indicators
 * - Customizable columns
 * - Loading and empty states
 * - Drag and drop row reordering via TableDragHandle
 * - Proper TypeScript support
 */

import {
  Table,
  TableBody,
  TableHeader,
  TableRow as UITableRow,
} from '@/components/ui/table'
import { createLogger } from '@/lib/logger'
import { cn } from '@/lib/utils'
import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core'
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { createPortal } from 'react-dom'

import { TableContext, TableContextValue } from './context/TableContext'

import '@/styles/table-styles.css'

import { useDragDropSensors } from '@/hooks/useDragDropSensors'
import { formatDateUserTz } from '@/utils/date'
import { shouldIgnoreKeyboardEvent } from '@/utils/keyboard-utils'

import type {
  CellGrid,
  CellPasteTarget,
  CellPosition,
  CellRange,
} from './cell-grid'
import {
  areSameCell,
  getCellAt,
  getCellKey,
  getCellRangeBounds,
  getCellsInRange,
  getFillDownTargets,
  getPasteTargets,
  isCellInRange,
  moveCell,
  moveCellWithWrap,
  parseClipboardMatrix,
} from './cell-grid'
import { CompactAddColumnDropdown } from './components/CompactAddColumnDropdown'
import { TableCell } from './components/TableCell'
import { TableHeaderCell } from './components/TableHeader'
import { SortableTableRow, TableRow } from './components/TableRow'
import { useColumnResizing } from './hooks/useColumnResizing'
import { useTableColumns } from './hooks/useTableColumns'
// Import from the local modular structure
import {
  ColumnDropIndicator,
  ColumnMeta,
  DataTableColumn,
  DataTableProps,
  DataTableRef,
  NON_REORDERABLE_COLUMN_IDS,
  RowDropPosition,
  UseColumnResizingReturn,
  UseTableColumnsReturn,
} from './types'

const logger = createLogger('components/shared/DataTable/DataTable')

// Fixed row heights used by virtualization size estimates. Regular rows are
// h-10 (40px) via BaseTable's rowClassName; group header cells are h-[52px].
const DEFAULT_ROW_HEIGHT = 40
const GROUP_HEADER_ROW_HEIGHT = 52

/**
 * Resolve the scroll container for a table: the nearest sticky-header-wrapper
 * ancestor. Pages sometimes nest BaseTable's own wrapper inside a page-level
 * one — the nearest (BaseTable's own) is the height-constrained scroller in
 * that layout; outer page wrappers are unconstrained and never scroll.
 */
function getScrollWrapper(start: HTMLElement | null): HTMLElement | null {
  return start?.closest<HTMLElement>('.sticky-header-wrapper') ?? null
}

const parsePixelWidth = (value: unknown): number | null => {
  if (typeof value !== 'string') return null
  const match = value.trim().match(/^(\d+(?:\.\d+)?)px$/)
  return match ? Number(match[1]) : null
}

const getColumnLayoutWidth = (
  columnId: string,
  meta: ColumnMeta | undefined,
  columnWidths: Record<string, number>,
) => {
  const resizedWidth = columnWidths[columnId]
  if (typeof resizedWidth === 'number' && Number.isFinite(resizedWidth)) {
    return resizedWidth
  }

  return (
    parsePixelWidth(meta?.width) ??
    parsePixelWidth(meta?.minWidth) ??
    (columnId === 'view' ? 45 : columnId === '_customize' ? 60 : 150)
  )
}

// Removed unused type guard and memoization hook for drag overlay

// Context to pass drag handle props from the sortable group row to the drag handle in TaskGroupHeaderInline
export const GroupDragHandleContext = createContext<{
  attributes: ReturnType<typeof useSortable>['attributes']
  listeners: ReturnType<typeof useSortable>['listeners']
} | null>(null)

// Group header row - single useSortable for both drag and drop.
// Passes drag listeners to the handle via GroupDragHandleContext.
function SortableGroupHeaderRow({
  sortableId,
  isDraggable = false,
  dropIndicator,
  measureElement,
  children,
  ...props
}: {
  sortableId: string
  isDraggable?: boolean
  dropIndicator?: { targetId: string; position: string } | null
  measureElement?: (element: HTMLTableRowElement | null) => void
  children: React.ReactNode
} & React.ComponentProps<typeof UITableRow>) {
  const { setNodeRef, attributes, listeners, isDragging } = useSortable({
    id: sortableId,
    disabled: !isDraggable,
  })

  const mergedStyle = { transform: 'none', transition: 'none', ...props.style }
  const isDropTarget = dropIndicator?.targetId === sortableId
  const dropPosition = isDropTarget ? dropIndicator?.position : undefined

  const ctxValue = isDraggable ? { attributes, listeners } : null

  return (
    <GroupDragHandleContext.Provider value={ctxValue}>
      <UITableRow
        ref={element => {
          setNodeRef(element)
          measureElement?.(element)
        }}
        data-drag-id={sortableId}
        data-group-drop-position={dropPosition || undefined}
        data-group-dragging={isDragging || undefined}
        {...props}
        style={mergedStyle}
        className={cn(
          props.className,
          isDropTarget &&
            dropPosition === 'above' &&
            'border-t-2 border-t-primary',
          isDropTarget &&
            dropPosition === 'below' &&
            'border-b-2 border-b-primary',
        )}
      >
        {children}
      </UITableRow>
    </GroupDragHandleContext.Provider>
  )
}

// Export the types for backward compatibility
export type { DataTableColumn, DataTableRef, DataTableProps }
// Create a typed forwardRef version of DataTable
function DataTableWithRef<T>(
  props: DataTableProps<T>,
  ref: ForwardedRef<DataTableRef<T>>,
) {
  const {
    data,
    columns,
    onRowClick,
    isLoading = false,
    loadingRows = new Set(),
    emptyState,
    className,
    rowClassName,
    enableSorting = false,
    manualSorting = false,
    enableFiltering = false,
    enableRowSelection = false,
    enableColumnVisibility = false,
    enableCellSelection = true,
    enableColumnResizing = false,
    onSortingChange,
    onColumnResize,
    onColumnResizeEnd,
    syncColumnWidths = false,
    getRowId = (row: T) => {
      const id = (row as { id?: string })?.id
      if (!id) {
        logger.warn(
          'Row is missing an id property. Using fallback string conversion.',
        )
        return String(row)
      }
      return id
    },
    hideHeader = false,
    enableVirtualization = false,
    // Group expand/collapse functionality
    enableGroupExpansion = false,
    defaultExpandedGroups = new Set(),
    onGroupExpansionChange,
    getGroupId,
    // Drag and drop functionality
    enableRowReordering = false,
    isDraggableRow,
    onRowReorder,
    onDragOver,
    onDragEnd,
    getDragId,
    droppableGroupHeaders = false,
    externalDropSession,
    dropIndicator,
    showDragHandle = false,
    renderDragOverlay,
    sortableRowIds: sortableRowIdsOverride,
    // Customize column functionality
    enableCustomizeColumn = false,
    onOpenColumnSettings,
    onAddCustomField,
    // Column management functionality
    onHideColumn,
    onToggleSort,
    // Custom field editing functionality
    onEditField,
    // Inline column rename
    onRenameColumn,
    // Column filtering functionality
    columnFilters: externalColumnFilters,
    onColumnFilterChange,
    // Field type selection functionality
    fieldTypeOptions,
    onFieldTypeSelect,
    customizePopoverKey,
    state,
    hiddenColumns,
    onEnableColumn,
    // Column reordering functionality
    enableColumnReordering = false,
    onColumnReorder,
    // Append row - renders as the last row in the table body
    appendRow,
    // Hide column menu option
    hideColumnMenu = false,
    // Table customizer props
    allColumns,
    onTableColumnVisibilityChange,
    onTableColumnsReorder,
    showCustomizeButton = false,
    canModifyColumns = true,
    showPendingUpdateIndicators = true,
    renderCustomRow,
    onCellPaste,
    onCellsPaste,
    onCellClear,
    onCellsClear,
  } = props

  // Generate a stable unique ID for this table instance
  // This is used for registering popovers with the global table editing store
  const tableId = useId()

  // Memoize the table context value to prevent unnecessary re-renders
  const tableContextValue = useMemo<TableContextValue>(
    () => ({
      tableId,
    }),
    [tableId],
  )

  // Table state
  const [sorting, setSorting] = useState<SortingState>(state?.sorting || [])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    state?.columnVisibility || {},
  )
  const [rowSelection, setRowSelection] = useState({})
  const [selectedCell, setSelectedCell] = useState<CellPosition | null>(null)
  const selectedCellRef = useRef<CellPosition | null>(null)
  const [selectedRange, setSelectedRange] = useState<CellRange | null>(null)
  const selectedRangeRef = useRef<CellRange | null>(null)
  const cellGridRef = useRef<CellGrid>({ rowIds: [], columnIds: [] })
  const fillTargetsRef = useRef<CellPasteTarget[]>([])
  const selectionDragCleanupRef = useRef<(() => void) | null>(null)
  const [fillPreviewCellKeys, setFillPreviewCellKeys] = useState<Set<string>>(
    () => new Set(),
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const showEmptyStateInBody = data.length === 0
  const [emptyStateViewportStyle, setEmptyStateViewportStyle] =
    useState<CSSProperties>({ left: 0, width: '100%' })

  // Column filter state (for custom filtering outside of TanStack's built-in filtering)
  const [internalColumnFilters, setInternalColumnFilters] = useState<
    Record<string, any>
  >(externalColumnFilters || {})

  // Sync sorting state when it changes from external source (e.g., saved preferences)
  // Use a ref to track previous value to avoid re-running on internal changes
  const prevExternalSortingRef = useRef<string>('')
  useEffect(() => {
    if (state?.sorting) {
      const newSortStr = JSON.stringify(state.sorting)
      // Only update if external state changed (not from internal updates)
      if (newSortStr !== prevExternalSortingRef.current) {
        prevExternalSortingRef.current = newSortStr
        setSorting(state.sorting)
      }
    } else if (prevExternalSortingRef.current !== '') {
      // Clear sorting if external state is cleared
      prevExternalSortingRef.current = ''
      setSorting([])
    }
  }, [state?.sorting])

  // Sync internal column filters when external filters change (e.g., parent clears a filter from chip)
  const prevExternalFiltersRef = useRef<string>(
    JSON.stringify(externalColumnFilters || {}),
  )
  useEffect(() => {
    const newFiltersStr = JSON.stringify(externalColumnFilters || {})
    // Only update if external state changed
    if (newFiltersStr !== prevExternalFiltersRef.current) {
      prevExternalFiltersRef.current = newFiltersStr
      setInternalColumnFilters(externalColumnFilters || {})
    }
  }, [externalColumnFilters])

  // Group expansion state - track current expansion state internally
  const [currentExpandedGroups, setCurrentExpandedGroups] = useState<
    Set<string>
  >(defaultExpandedGroups)

  // Simplified drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null)
  const rowDragStartRectRef = useRef<{ top: number; height: number } | null>(
    null,
  )
  const activeRowIdRef = useRef<string | null>(null)
  const lastPointerYRef = useRef<number | null>(null)
  // True while the pointer hovers an external drop target during a row drag
  // (see ExternalRowDropSession) — internal drop-target resolution is
  // suspended so the external target and the row indicator never compete.
  const externalDropHoverRef = useRef(false)
  const rowDragListenerCleanupRef = useRef<(() => void) | null>(null)
  const [rowDropTarget, setRowDropTarget] = useState<{
    targetId: string
    position: RowDropPosition
  } | null>(null)

  // Column reordering state
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null)
  const [columnDropIndicator, setColumnDropIndicator] =
    useState<ColumnDropIndicator | null>(null)
  const columnDragStartRectRef = useRef<{
    left: number
    width: number
    pointerStartX: number | null
  } | null>(null)

  useEffect(() => {
    selectedCellRef.current = selectedCell
  }, [selectedCell])

  useEffect(() => {
    selectedRangeRef.current = selectedRange
  }, [selectedRange])

  const clearCellSelection = useCallback(() => {
    selectionDragCleanupRef.current?.()
    selectedCellRef.current = null
    selectedRangeRef.current = null
    setSelectedCell(null)
    setSelectedRange(null)
    fillTargetsRef.current = []
    setFillPreviewCellKeys(new Set())
  }, [])

  // Draft-row mode: after an inline add-task Tab-commit, the freshly created
  // row is put in a keyboard "walk-through" session — Tab both moves the cell
  // selection AND opens the next cell's editor. The session ends (and a
  // 'datatable-draft-row-end' event notifies the row's owner) as soon as the
  // selection or a pointer interaction leaves the row.
  const draftRowIdRef = useRef<string | null>(null)

  const endDraftRowSession = useCallback(() => {
    const rowId = draftRowIdRef.current
    if (!rowId) return
    draftRowIdRef.current = null
    window.dispatchEvent(
      new CustomEvent('datatable-draft-row-end', { detail: { rowId } }),
    )
  }, [])

  useEffect(() => {
    return () => {
      selectionDragCleanupRef.current?.()
    }
  }, [])

  useEffect(() => {
    if (!enableCellSelection && selectedCell) {
      selectedCellRef.current = null
      selectedRangeRef.current = null
      setSelectedCell(null)
      setSelectedRange(null)
    }
  }, [enableCellSelection, selectedCell])

  // Setup drag and drop sensors with proper activation constraints
  const { sensors } = useDragDropSensors({
    activationDistance: 12, // Increase threshold so text selection doesn't trigger drag
  })

  // Column resizing hook
  const {
    columnWidths,
    isActivelyResizing,
    handleColumnResize,
    handleColumnResizeEnd,
    resetColumnWidths,
  }: UseColumnResizingReturn = useColumnResizing({
    initialColumnWidths: state?.columnWidths,
    onColumnResize,
    onColumnResizeEnd,
    syncInitialColumnWidths: syncColumnWidths,
  })

  // Table columns hook
  const { columnsWithCustomize }: UseTableColumnsReturn<T> = useTableColumns<T>(
    {
      columns,
      enableSorting,
      enableFiltering,
      enableColumnResizing,
      enableCustomizeColumn,
      onSortingChange,
      fieldTypeOptions,
      onFieldTypeSelect,
      hiddenColumns,
      onEnableColumn,
      currentSorting: sorting, // Pass current sorting state for header indicators
      customizePopoverKey,
      // Table customizer props
      allColumns,
      onColumnVisibilityChange: onTableColumnVisibilityChange,
      onColumnsReorder: onTableColumnsReorder,
      showCustomizeButton,
      canModifyColumns,
    },
  )

  const getCellSelector = useCallback((rowId: string, columnId: string) => {
    const safeRowId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(rowId) : rowId
    const safeColumnId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(columnId) : columnId
    return `tr[data-row-id="${safeRowId}"] td[data-column="${safeColumnId}"]`
  }, [])

  const getColumnHeaderElement = useCallback((columnId: string) => {
    const safeColumnId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(columnId) : columnId
    return (
      containerRef.current?.querySelector<HTMLTableCellElement>(
        `th[data-column="${safeColumnId}"]`,
      ) ?? null
    )
  }, [])

  const updateEmptyStateViewport = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const viewport =
      getScrollWrapper(container) ??
      container.querySelector<HTMLElement>('.data-table-scroll') ??
      container
    const containerRect = container.getBoundingClientRect()
    const viewportRect = viewport.getBoundingClientRect()
    const left = Math.max(0, viewportRect.left - containerRect.left)
    const width = viewport.clientWidth || viewportRect.width

    setEmptyStateViewportStyle(previous => {
      if (previous.left === left && previous.width === width) {
        return previous
      }

      return { left, width }
    })
  }, [])

  useLayoutEffect(() => {
    if (!showEmptyStateInBody) return

    const container = containerRef.current
    if (!container) return

    const viewport =
      getScrollWrapper(container) ??
      container.querySelector<HTMLElement>('.data-table-scroll') ??
      container

    updateEmptyStateViewport()
    viewport.addEventListener('scroll', updateEmptyStateViewport, {
      passive: true,
    })
    window.addEventListener('resize', updateEmptyStateViewport)

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateEmptyStateViewport)
        : null
    resizeObserver?.observe(container)
    resizeObserver?.observe(viewport)

    return () => {
      viewport.removeEventListener('scroll', updateEmptyStateViewport)
      window.removeEventListener('resize', updateEmptyStateViewport)
      resizeObserver?.disconnect()
    }
  }, [showEmptyStateInBody, updateEmptyStateViewport])

  const getRowElement = useCallback((dragId: string) => {
    const safeDragId =
      typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(dragId) : dragId
    return (
      containerRef.current?.querySelector<HTMLTableRowElement>(
        `tr[data-drag-id="${safeDragId}"]`,
      ) ?? null
    )
  }, [])

  const ensureCellInView = useCallback((cell: HTMLTableCellElement | null) => {
    if (!cell) return
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' })

    // Horizontal scrolling lives on the outermost sticky-header wrapper when
    // present (single scroll container so the sticky thead works); fall back
    // to the inner scroller for standalone DataTable usages.
    const scrollContainer =
      getScrollWrapper(containerRef.current) ??
      containerRef.current?.querySelector<HTMLElement>('.data-table-scroll')
    if (!scrollContainer) return

    const containerRect = scrollContainer.getBoundingClientRect()
    const cellRect = cell.getBoundingClientRect()
    const row = cell.closest('tr')
    const stickyCell = row?.querySelector('td:first-child')
    const stickyRect = stickyCell?.getBoundingClientRect()
    const stickyWidth = stickyRect ? stickyRect.width : 0
    const leftBoundary = containerRect.left + stickyWidth

    let nextScrollLeft = scrollContainer.scrollLeft
    if (cellRect.left < leftBoundary) {
      nextScrollLeft -= leftBoundary - cellRect.left
    } else if (cellRect.right > containerRect.right) {
      nextScrollLeft += cellRect.right - containerRect.right
    }

    const maxScrollLeft =
      scrollContainer.scrollWidth - scrollContainer.clientWidth
    if (maxScrollLeft <= 0) return
    nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, nextScrollLeft))

    if (nextScrollLeft !== scrollContainer.scrollLeft) {
      scrollContainer.scrollLeft = nextScrollLeft
    }
  }, [])

  const findCellElement = useCallback(
    (rowId: string, columnId: string) => {
      if (!containerRef.current) return null
      return containerRef.current.querySelector<HTMLTableCellElement>(
        getCellSelector(rowId, columnId),
      )
    },
    [getCellSelector],
  )

  const getCurrentCellGrid = useCallback((): CellGrid => {
    const currentGrid = cellGridRef.current
    if (currentGrid.rowIds.length > 0 && currentGrid.columnIds.length > 0) {
      return currentGrid
    }

    const rows = Array.from(
      containerRef.current?.querySelectorAll<HTMLTableRowElement>(
        'tbody tr[data-row-id]',
      ) ?? [],
    ).filter(row => {
      if (row.classList.contains('group-header-row')) return false
      if (row.classList.contains('append-row')) return false
      return Boolean(row.querySelector('td[data-column]'))
    })

    const rowIds = rows
      .map(row => row.getAttribute('data-row-id'))
      .filter((rowId): rowId is string => Boolean(rowId))
    const columnIds = Array.from(
      rows[0]?.querySelectorAll<HTMLTableCellElement>('td[data-column]') ?? [],
    )
      .map(cell => cell.getAttribute('data-column'))
      .filter((columnId): columnId is string => Boolean(columnId))

    return { rowIds, columnIds }
  }, [])

  useEffect(() => {
    if (!selectedCell) return
    const cell = findCellElement(selectedCell.rowId, selectedCell.columnId)
    if (!cell) {
      clearCellSelection()
    }
  }, [
    selectedCell,
    data,
    columnsWithCustomize,
    findCellElement,
    clearCellSelection,
  ])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target || !containerRef.current) return

      // Pointer interactions outside the draft row end its walk-through
      // session — except inside portaled editor popovers, which belong to the
      // row's open editor.
      if (draftRowIdRef.current && target instanceof HTMLElement) {
        const inPortaledEditor = target.closest(
          '[data-radix-popper-content-wrapper], [data-hierarchical-module-search-dropdown="true"]',
        )
        const row = target.closest('tr[data-row-id]')
        if (
          !inPortaledEditor &&
          (!row || row.getAttribute('data-row-id') !== draftRowIdRef.current)
        ) {
          endDraftRowSession()
        }
      }

      if (!selectedCellRef.current) return
      if (
        target instanceof HTMLElement &&
        target.closest('[data-hierarchical-module-search-dropdown="true"]')
      ) {
        return
      }
      if (!containerRef.current.contains(target)) {
        clearCellSelection()
      }
    }

    document.addEventListener('mousedown', handlePointerDown, true)
    return () =>
      document.removeEventListener('mousedown', handlePointerDown, true)
  }, [clearCellSelection, endDraftRowSession])

  const activateCellEdit = useCallback(
    (cell: HTMLTableCellElement | null) => {
      if (!cell) return

      // Priority 1: Elements with explicit editable marker (EditableCell pattern)
      const editable = cell.querySelector<HTMLElement>(
        '[data-editable-cell="true"]',
      )
      if (editable) {
        editable.setAttribute('data-edit-on-focus', 'true')
        editable.focus({ preventScroll: true })
        // Also click as fallback - the click handler checks for data-edit-on-focus
        editable.click()
        ensureCellInView(cell)
        return
      }

      // Priority 2: Elements with edit trigger marker
      const trigger = cell.querySelector<HTMLElement>(
        '[data-edit-trigger="true"]',
      )
      if (trigger) {
        trigger.setAttribute('data-edit-on-focus', 'true')
        trigger.focus({ preventScroll: true })
        // Also click as fallback
        trigger.click()
        ensureCellInView(cell)
        return
      }

      // Priority 3: Radix UI triggers and standard interactive elements
      const radixOrInteractive = cell.querySelector<HTMLElement>(
        '[data-radix-popover-trigger], [data-radix-dropdown-menu-trigger], [data-radix-select-trigger], [data-radix-dialog-trigger], [role="combobox"], [role="listbox"]',
      )
      if (radixOrInteractive) {
        radixOrInteractive.click()
        radixOrInteractive.focus?.({ preventScroll: true })
        ensureCellInView(cell)
        return
      }

      // Priority 4: Buttons (excluding hidden/disabled)
      const button = cell.querySelector<HTMLElement>(
        'button:not([disabled]):not(.opacity-0):not([aria-hidden="true"])',
      )
      if (button) {
        button.click()
        button.focus?.({ preventScroll: true })
        ensureCellInView(cell)
        return
      }

      // Priority 5: Input elements
      const input = cell.querySelector<HTMLInputElement>(
        'input:not([disabled]), textarea:not([disabled])',
      )
      if (input) {
        input.focus({ preventScroll: true })
        input.select?.()
        ensureCellInView(cell)
        return
      }

      // Priority 6: Clickable divs (common pattern for Popover triggers with asChild)
      // Look for divs that have cursor-pointer class or are direct wrappers
      const clickableDiv = cell.querySelector<HTMLElement>(
        'div[class*="cursor-pointer"]:not([disabled]), div.hover\\:bg-muted, div.hover\\:bg-accent',
      )
      if (clickableDiv) {
        clickableDiv.click()
        ensureCellInView(cell)
        return
      }

      // Priority 7: Any element with role="button"
      const roleButton = cell.querySelector<HTMLElement>(
        '[role="button"]:not([disabled])',
      )
      if (roleButton) {
        roleButton.click()
        roleButton.focus?.({ preventScroll: true })
        ensureCellInView(cell)
      }
    },
    [ensureCellInView],
  )

  const updateCellSelection = useCallback(
    (
      nextCell: CellPosition,
      options: { extend?: boolean; cellElement?: HTMLTableCellElement } = {},
    ) => {
      if (draftRowIdRef.current && nextCell.rowId !== draftRowIdRef.current) {
        endDraftRowSession()
      }
      const anchor = options.extend
        ? selectedRangeRef.current?.anchor ||
          selectedCellRef.current ||
          nextCell
        : nextCell
      const nextRange = { anchor, focus: nextCell }

      selectedCellRef.current = nextCell
      selectedRangeRef.current = nextRange
      setSelectedCell(nextCell)
      setSelectedRange(nextRange)

      const cellElement =
        options.cellElement ||
        findCellElement(nextCell.rowId, nextCell.columnId)
      cellElement?.focus({ preventScroll: true })
      ensureCellInView(cellElement ?? null)
    },
    [endDraftRowSession, ensureCellInView, findCellElement],
  )

  const handleCellSelect = useCallback(
    (
      rowId: string,
      columnId: string,
      cell: HTMLTableCellElement,
      options?: { extend?: boolean; startDrag?: boolean },
    ) => {
      updateCellSelection(
        { rowId, columnId },
        { extend: options?.extend, cellElement: cell },
      )

      if (options?.startDrag) {
        selectionDragCleanupRef.current?.()

        const previousUserSelect = document.body.style.userSelect
        document.body.style.userSelect = 'none'

        const getCellFromMouseEvent = (event: MouseEvent) => {
          if (typeof document.elementFromPoint !== 'function') return null

          const element = document.elementFromPoint(
            event.clientX,
            event.clientY,
          )
          const targetCell =
            element?.closest<HTMLTableCellElement>('td[data-column]')
          const targetRow =
            targetCell?.closest<HTMLTableRowElement>('tr[data-row-id]')
          if (
            !targetCell ||
            !targetRow ||
            !containerRef.current?.contains(targetCell)
          ) {
            return null
          }

          const nextRowId = targetRow.getAttribute('data-row-id')
          const nextColumnId = targetCell.getAttribute('data-column')
          if (!nextRowId || !nextColumnId) return null

          return { rowId: nextRowId, columnId: nextColumnId }
        }

        const preventTextSelection = (event: Event) => {
          event.preventDefault()
        }

        const cleanup = () => {
          document.removeEventListener('mousemove', handleMouseMove)
          document.removeEventListener('mouseup', handleMouseUp)
          document.removeEventListener('selectstart', preventTextSelection)
          document.body.style.userSelect = previousUserSelect
          if (selectionDragCleanupRef.current === cleanup) {
            selectionDragCleanupRef.current = null
          }
        }

        const handleMouseMove = (event: MouseEvent) => {
          event.preventDefault()
          const nextCell = getCellFromMouseEvent(event)
          if (!nextCell) return
          updateCellSelection(nextCell, { extend: true })
        }

        const handleMouseUp = () => {
          cleanup()
        }

        selectionDragCleanupRef.current = cleanup
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
        document.addEventListener('selectstart', preventTextSelection)
      }
    },
    [updateCellSelection],
  )

  const moveSelection = useCallback(
    (
      direction: 'up' | 'down' | 'left' | 'right',
      options: { extend?: boolean } = {},
    ) => {
      const current = selectedCellRef.current
      if (!current) return

      const nextCell = moveCell(getCurrentCellGrid(), current, direction)
      if (!nextCell || areSameCell(current, nextCell)) return

      updateCellSelection(nextCell, { extend: options.extend })
    },
    [getCurrentCellGrid, updateCellSelection],
  )

  // Move selection with row wrapping (for Tab navigation). Returns the newly
  // selected cell so draft-row mode can auto-open its editor.
  const moveSelectionWithWrap = useCallback(
    (direction: 'next' | 'prev'): CellPosition | null => {
      let current = selectedCellRef.current

      if (!current && containerRef.current) {
        const activeElement = document.activeElement as HTMLElement | null
        const closestCell =
          activeElement?.closest<HTMLTableCellElement>('td[data-column]')
        const closestRow =
          closestCell?.closest<HTMLTableRowElement>('tr[data-row-id]')
        const rowId = closestRow?.getAttribute('data-row-id')
        const columnId = closestCell?.getAttribute('data-column')

        if (
          closestCell &&
          rowId &&
          columnId &&
          containerRef.current.contains(closestCell)
        ) {
          current = { rowId, columnId }
        }
      }

      if (!current) return null

      const nextCell = moveCellWithWrap(
        getCurrentCellGrid(),
        current,
        direction,
      )
      if (!nextCell) return null

      updateCellSelection(nextCell)
      return nextCell
    },
    [getCurrentCellGrid, updateCellSelection],
  )

  // Helper to get the display value of a cell for copying
  const getCellDisplayValue = useCallback(
    (rowId: string, columnId: string): string => {
      // Find the row data
      const rowData = data.find(row => getRowId(row) === rowId)
      if (!rowData) return ''

      // Find the column definition
      const columnDef = columns.find(col => col.id === columnId)
      if (!columnDef) return ''

      // Get the raw value using the accessor function or direct property access
      let value: unknown
      if (columnDef.accessorFn) {
        value = columnDef.accessorFn(rowData)
      } else {
        // Try to get the value from the row data using the column id
        value = (rowData as Record<string, unknown>)[columnId]
      }

      // Convert to display string
      if (value === null || value === undefined) return ''
      if (typeof value === 'string') return value
      if (typeof value === 'number' || typeof value === 'boolean')
        return String(value)
      if (value instanceof Date) return formatDateUserTz(value)
      if (Array.isArray(value)) {
        // For arrays (like tags), join with commas
        return value
          .map(item => {
            if (typeof item === 'object' && item !== null) {
              return (
                (item as Record<string, unknown>).name ||
                (item as Record<string, unknown>).label ||
                String(item)
              )
            }
            return String(item)
          })
          .join(', ')
      }
      if (typeof value === 'object') {
        // For objects, try to get a display name
        const obj = value as Record<string, unknown>
        return (
          (obj.name as string) ||
          (obj.label as string) ||
          (obj.title as string) ||
          JSON.stringify(value)
        )
      }
      return String(value)
    },
    [data, columns, getRowId],
  )

  const getSelectedCells = useCallback(() => {
    const grid = getCurrentCellGrid()
    const range = selectedRangeRef.current
    const current = selectedCellRef.current
    if (range) return getCellsInRange(grid, range)
    return current ? [current] : []
  }, [getCurrentCellGrid])

  const copySelectedCells = useCallback(async () => {
    const grid = getCurrentCellGrid()
    const range =
      selectedRangeRef.current ||
      (selectedCellRef.current
        ? { anchor: selectedCellRef.current, focus: selectedCellRef.current }
        : null)
    if (!range) return

    const bounds = getCellRangeBounds(grid, range)
    if (!bounds) return

    const rows: string[] = []
    for (
      let rowIndex = bounds.startRowIndex;
      rowIndex <= bounds.endRowIndex;
      rowIndex += 1
    ) {
      const rowValues: string[] = []
      for (
        let columnIndex = bounds.startColumnIndex;
        columnIndex <= bounds.endColumnIndex;
        columnIndex += 1
      ) {
        const cell = getCellAt(grid, rowIndex, columnIndex)
        rowValues.push(
          cell ? getCellDisplayValue(cell.rowId, cell.columnId) : '',
        )
      }
      rows.push(rowValues.join('\t'))
    }

    try {
      await navigator.clipboard.writeText(rows.join('\n'))
    } catch (err) {
      logger.warn('Failed to copy to clipboard:', err)
    }
  }, [getCellDisplayValue, getCurrentCellGrid])

  const applyPasteTargets = useCallback(
    async (targets: CellPasteTarget[]) => {
      if (targets.length === 0) return

      if (onCellsPaste) {
        await onCellsPaste(targets)
        return
      }

      if (!onCellPaste) return

      for (const target of targets) {
        await onCellPaste(target.rowId, target.columnId, target.text)
      }
    },
    [onCellPaste, onCellsPaste],
  )

  const pasteSelectedCells = useCallback(
    async (text: string) => {
      const current = selectedCellRef.current
      if (!current) return

      const targets = getPasteTargets({
        grid: getCurrentCellGrid(),
        start: current,
        matrix: parseClipboardMatrix(text),
        selection: selectedRangeRef.current,
      })

      await applyPasteTargets(targets)
    },
    [applyPasteTargets, getCurrentCellGrid],
  )

  const clearSelectedCells = useCallback(async () => {
    const cells = getSelectedCells()
    if (cells.length === 0) return

    if (onCellsClear) {
      await onCellsClear(cells)
      return
    }

    if (!onCellClear) return

    for (const cell of cells) {
      await onCellClear(cell.rowId, cell.columnId)
    }
  }, [getSelectedCells, onCellClear, onCellsClear])

  const getCellFromPointer = useCallback(
    (clientX: number, clientY: number): CellPosition | null => {
      if (typeof document.elementFromPoint !== 'function') return null

      const element = document.elementFromPoint(clientX, clientY)
      const cell = element?.closest<HTMLTableCellElement>('td[data-column]')
      const row = cell?.closest<HTMLTableRowElement>('tr[data-row-id]')
      if (!cell || !row || !containerRef.current?.contains(cell)) return null

      const rowId = row.getAttribute('data-row-id')
      const columnId = cell.getAttribute('data-column')
      if (!rowId || !columnId) return null

      return { rowId, columnId }
    },
    [],
  )

  const buildFillTargets = useCallback(
    (target: CellPosition) => {
      const current = selectedCellRef.current
      if (!current) return []

      return getFillDownTargets({
        grid: getCurrentCellGrid(),
        source: selectedRangeRef.current || { anchor: current, focus: current },
        target,
        getValue: cell => getCellDisplayValue(cell.rowId, cell.columnId),
      })
    },
    [getCellDisplayValue, getCurrentCellGrid],
  )

  const handleFillHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button && event.button !== 0) return

      event.preventDefault()
      event.stopPropagation()

      fillTargetsRef.current = []
      setFillPreviewCellKeys(new Set())

      const updatePreview = (clientX: number, clientY: number) => {
        const pointerCell = getCellFromPointer(clientX, clientY)
        const targets = pointerCell ? buildFillTargets(pointerCell) : []
        fillTargetsRef.current = targets
        setFillPreviewCellKeys(new Set(targets.map(getCellKey)))
      }

      const resetFillState = () => {
        fillTargetsRef.current = []
        setFillPreviewCellKeys(new Set())
      }

      const removeListeners = () => {
        document.removeEventListener('pointermove', handlePointerMove)
        document.removeEventListener('pointerup', handlePointerUp)
        document.removeEventListener('pointercancel', handlePointerCancel)
      }

      const handlePointerMove = (moveEvent: PointerEvent) => {
        updatePreview(moveEvent.clientX, moveEvent.clientY)
      }

      const handlePointerUp = (upEvent: PointerEvent) => {
        updatePreview(upEvent.clientX, upEvent.clientY)
        removeListeners()

        const targets = fillTargetsRef.current
        resetFillState()

        Promise.resolve(applyPasteTargets(targets)).catch(err => {
          logger.warn('Failed to fill selected cells:', err)
        })
      }

      const handlePointerCancel = () => {
        removeListeners()
        resetFillState()
      }

      document.addEventListener('pointermove', handlePointerMove)
      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerCancel)
    },
    [applyPasteTargets, buildFillTargets, getCellFromPointer],
  )

  useEffect(() => {
    if (!enableCellSelection) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (shouldIgnoreKeyboardEvent(event)) return

      let current = selectedCellRef.current

      if (!current && containerRef.current) {
        const target = event.target as HTMLElement | null
        const activeTarget =
          target || (document.activeElement as HTMLElement | null)
        const closestCell =
          activeTarget?.closest<HTMLTableCellElement>('td[data-column]')
        const closestRow = closestCell?.closest('tr')
        const rowId = closestRow?.getAttribute('data-row-id')
        const columnId = closestCell?.getAttribute('data-column')

        if (
          closestCell &&
          rowId &&
          columnId &&
          containerRef.current.contains(closestCell)
        ) {
          current = { rowId, columnId }
          updateCellSelection(current, { cellElement: closestCell })
        }
      }

      if (!current) return

      const lowerKey = event.key.toLowerCase()

      if ((event.metaKey || event.ctrlKey) && lowerKey === 'c') {
        event.preventDefault()
        copySelectedCells()
        return
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        lowerKey === 'v' &&
        (onCellPaste || onCellsPaste)
      ) {
        event.preventDefault()
        navigator.clipboard
          .readText()
          .then(text => {
            if (!text) return
            return pasteSelectedCells(text)
          })
          .catch(err => {
            logger.warn('Failed to read clipboard for paste:', err)
          })
        return
      }

      if (event.key === 'Escape') {
        // A portaled editor popover owns this Escape (Radix closes it) —
        // keep the cell selection and any draft-row session alive so Tab
        // continues walking the row afterwards.
        if (document.querySelector('[data-radix-popper-content-wrapper]')) {
          return
        }
        event.preventDefault()
        endDraftRowSession()
        clearCellSelection()
        return
      }

      if (
        (onCellClear || onCellsClear) &&
        (event.key === 'Delete' || event.key === 'Backspace') &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey
      ) {
        event.preventDefault()
        Promise.resolve(clearSelectedCells()).catch(err => {
          logger.warn('Failed to clear selected cells:', err)
        })
        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()
        const cell = findCellElement(current.rowId, current.columnId)
        activateCellEdit(cell)
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        moveSelection('up', { extend: event.shiftKey })
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        moveSelection('down', { extend: event.shiftKey })
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        moveSelection('left', { extend: event.shiftKey })
        return
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        moveSelection('right', { extend: event.shiftKey })
        return
      }

      if (event.key === 'Tab') {
        event.preventDefault()
        const direction = event.shiftKey ? 'prev' : 'next'
        const nextCell = moveSelectionWithWrap(direction)
        // Draft-row walk-through: Tab within the fresh row opens each cell's
        // editor directly. (Selection moving off the row already ended the
        // session inside updateCellSelection.)
        if (
          nextCell &&
          draftRowIdRef.current &&
          nextCell.rowId === draftRowIdRef.current
        ) {
          activateCellEdit(findCellElement(nextCell.rowId, nextCell.columnId))
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    enableCellSelection,
    activateCellEdit,
    clearCellSelection,
    clearSelectedCells,
    copySelectedCells,
    endDraftRowSession,
    findCellElement,
    moveSelection,
    moveSelectionWithWrap,
    onCellClear,
    onCellPaste,
    onCellsClear,
    onCellsPaste,
    pasteSelectedCells,
    updateCellSelection,
  ])

  // Listen for custom table cell navigation events (from EditableCell Tab handling)
  useEffect(() => {
    if (!enableCellSelection) return

    const container = containerRef.current
    if (!container) return

    const handleCellNavigate = (event: Event) => {
      const customEvent = event as CustomEvent<{ direction: 'next' | 'prev' }>
      moveSelectionWithWrap(customEvent.detail.direction)
    }

    container.addEventListener('tableCellNavigate', handleCellNavigate)
    return () =>
      container.removeEventListener('tableCellNavigate', handleCellNavigate)
  }, [enableCellSelection, moveSelectionWithWrap])

  // Draft-row activation: an inline add-task Tab-commit dispatches this event
  // right after inserting the optimistic row into the query cache. Wait (via
  // rAF retries) for the row to render, then select and open the first
  // editable cell so the user tabs straight through the remaining columns.
  useEffect(() => {
    if (!enableCellSelection) return

    let rafId: number | null = null

    const handleDraftRowActivate = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          rowId?: string
          skipColumnIds?: string[]
          focus?: boolean
        }>
      ).detail
      const rowId = detail?.rowId
      if (!rowId) return
      const skipColumnIds = new Set(detail?.skipColumnIds ?? [])
      // focus=false registers the draft session (row is mouse-editable, ends
      // on outside interaction) without moving keyboard focus into the row —
      // used by Enter-commit where focus stays in the add input.
      const shouldFocus = detail?.focus !== false

      let attempts = 0
      const tryActivate = () => {
        rafId = null
        const container = containerRef.current
        if (!container) return
        const safeRowId =
          typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(rowId) : rowId
        const row = container.querySelector<HTMLTableRowElement>(
          `tr[data-row-id="${safeRowId}"]`,
        )
        if (!row) {
          // The optimistic row may not have rendered yet — retry briefly.
          // Rows belonging to another table on the page never appear here,
          // so give up quietly.
          if (attempts < 40) {
            attempts += 1
            rafId = requestAnimationFrame(tryActivate)
          }
          return
        }

        // A previous draft session (e.g. rapid consecutive adds) is over now.
        if (draftRowIdRef.current && draftRowIdRef.current !== rowId) {
          endDraftRowSession()
        }
        draftRowIdRef.current = rowId

        if (!shouldFocus) return

        const cells = Array.from(
          row.querySelectorAll<HTMLTableCellElement>('td[data-column]'),
        )
        const targetCell = cells.find(cell => {
          const columnId = cell.getAttribute('data-column')
          if (!columnId || skipColumnIds.has(columnId)) return false
          return Boolean(
            cell.querySelector(
              '[data-edit-trigger="true"], [data-editable-cell="true"]',
            ),
          )
        })
        if (!targetCell) return
        const columnId = targetCell.getAttribute('data-column')
        if (!columnId) return

        updateCellSelection({ rowId, columnId }, { cellElement: targetCell })
        activateCellEdit(targetCell)
      }

      tryActivate()
    }

    window.addEventListener(
      'datatable-draft-row-activate',
      handleDraftRowActivate,
    )
    return () => {
      window.removeEventListener(
        'datatable-draft-row-activate',
        handleDraftRowActivate,
      )
      if (rafId !== null) cancelAnimationFrame(rafId)
      endDraftRowSession()
    }
  }, [
    enableCellSelection,
    activateCellEdit,
    endDraftRowSession,
    updateCellSelection,
  ])

  // Simplified drag start handler
  const handleDragStart = (event: DragStartEvent) => {
    const dragId = String(event.active.id)
    setActiveId(dragId)
    setRowDropTarget(null)
    activeRowIdRef.current = dragId

    const rowElement = getRowElement(dragId)
    if (rowElement) {
      const rect = rowElement.getBoundingClientRect()
      rowDragStartRectRef.current = {
        top: rect.top,
        height: rect.height,
      }
    } else {
      rowDragStartRectRef.current = null
    }

    // Seed the pointer position from the activator event so the very first
    // drop-target resolution uses the real cursor Y rather than the dragged
    // row's center (which is offset by however far from center the row was
    // grabbed — the root cause of "doesn't drop where I dropped it").
    const activator = event.activatorEvent
    if (
      activator instanceof MouseEvent ||
      (typeof PointerEvent !== 'undefined' && activator instanceof PointerEvent)
    ) {
      lastPointerYRef.current = activator.clientY
    } else if (rowDragStartRectRef.current) {
      lastPointerYRef.current =
        rowDragStartRectRef.current.top + rowDragStartRectRef.current.height / 2
    }

    externalDropHoverRef.current = false
    externalDropSession?.onStart(dragId)

    // Track the live cursor position for the duration of the drag and
    // re-resolve the drop target against it. dnd-kit's onDragMove reports the
    // dragged element's translated rect, not the pointer, so we listen to the
    // real pointer here.
    const handlePointerMove = (pointerEvent: PointerEvent) => {
      const activeRowId = activeRowIdRef.current
      if (activeRowId === null) return
      lastPointerYRef.current = pointerEvent.clientY
      if (externalDropSession) {
        externalDropHoverRef.current = externalDropSession.onMove({
          x: pointerEvent.clientX,
          y: pointerEvent.clientY,
        })
      }
      updateRowDropTarget(activeRowId, pointerEvent.clientY)
    }

    // Recompute the drop target whenever the list scrolls under a held
    // pointer. dnd-kit's onDragMove only fires on pointer movement, so during
    // auto-scroll the candidate rows shift while the pointer stays put — the
    // drop target must be re-resolved against the live row positions using the
    // last known pointer position.
    const handleScroll = () => {
      const activeRowId = activeRowIdRef.current
      const pointerY = lastPointerYRef.current
      if (activeRowId === null || pointerY === null) return
      updateRowDropTarget(activeRowId, pointerY)
    }
    window.addEventListener('pointermove', handlePointerMove, true)
    window.addEventListener('scroll', handleScroll, true)
    rowDragListenerCleanupRef.current = () => {
      window.removeEventListener('pointermove', handlePointerMove, true)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }

  // Column filter change handler
  const handleColumnFilterChange = (columnId: string, value: any) => {
    setInternalColumnFilters(prev => {
      const newFilters = { ...prev }
      if (value === null || value === undefined) {
        delete newFilters[columnId]
      } else {
        newFilters[columnId] = value
      }
      return newFilters
    })

    // Call external handler if provided
    if (onColumnFilterChange) {
      onColumnFilterChange(columnId, value)
    }
  }

  // Enhanced collision detection with mouse position tracking
  const customCollisionDetection: CollisionDetection = args => {
    // For now, use the existing collision detection but with better pointer tracking
    const pointerCollisions = pointerWithin(args)

    if (pointerCollisions.length > 0) {
      return pointerCollisions
    }

    // Fallback to rectangle intersection for better coverage
    const rectCollisions = rectIntersection(args)

    if (rectCollisions.length > 0) {
      return rectCollisions
    }

    // Final fallback to closest center
    return closestCenter(args)
  }

  const resolveRowDropTarget = useCallback(
    (
      activeRowId: string,
      dragCenterY: number,
    ): { targetId: string; position: RowDropPosition } | null => {
      const rowEntries = Array.from(
        containerRef.current?.querySelectorAll<HTMLTableRowElement>(
          'tr[data-drag-id]:not([data-drop-disabled="true"])',
        ) ?? [],
      )
        .map(row => ({
          targetId: row.getAttribute('data-drag-id'),
          rect: row.getBoundingClientRect(),
        }))
        .filter(
          (
            entry,
          ): entry is {
            targetId: string
            rect: DOMRect
          } => !!entry.targetId,
        )
        .filter((entry, index, entries) => {
          return (
            entries.findIndex(
              candidate => candidate.targetId === entry.targetId,
            ) === index
          )
        })

      if (rowEntries.length === 0) {
        return null
      }

      // Group-header drags target whole group blocks: a header's drop zone
      // spans from its own top down to the next header's top, so an expanded
      // group can be reordered by pointing anywhere inside it instead of
      // having to land on another header's thin row.
      const activeRow = getRowElement(activeRowId)
      if (activeRow?.hasAttribute('data-group-id')) {
        const headerEntries = Array.from(
          containerRef.current?.querySelectorAll<HTMLTableRowElement>(
            'tr[data-drag-id][data-group-id]:not([data-drop-disabled="true"])',
          ) ?? [],
        )
          .map(row => ({
            targetId: row.getAttribute('data-drag-id'),
            rect: row.getBoundingClientRect(),
          }))
          .filter(
            (entry): entry is { targetId: string; rect: DOMRect } =>
              !!entry.targetId,
          )
          .filter((entry, index, entries) => {
            return (
              entries.findIndex(
                candidate => candidate.targetId === entry.targetId,
              ) === index
            )
          })

        if (headerEntries.length === 0) {
          return null
        }

        const tableBottom = rowEntries.reduce(
          (max, entry) => Math.max(max, entry.rect.bottom),
          0,
        )
        const groupBlocks = headerEntries.map((entry, index) => {
          const bottom =
            headerEntries[index + 1]?.rect.top ??
            Math.max(entry.rect.bottom, tableBottom)
          return {
            targetId: entry.targetId,
            rect: {
              top: entry.rect.top,
              bottom,
              height: bottom - entry.rect.top,
            },
          }
        })

        const groupTarget = resolveVerticalDropTarget({
          activeId: activeRowId,
          pointerY: dragCenterY,
          candidates: groupBlocks,
        })

        if (!groupTarget) {
          return null
        }

        // Snap "below group X" to "above the next header" so the indicator
        // draws at the real group boundary rather than between X's header
        // and its first task.
        if (groupTarget.position === 'below') {
          const blockIndex = groupBlocks.findIndex(
            block => block.targetId === groupTarget.targetId,
          )
          const nextBlock = groupBlocks[blockIndex + 1]
          if (nextBlock && nextBlock.targetId !== activeRowId) {
            return { targetId: nextBlock.targetId, position: 'above' }
          }
        }

        return groupTarget
      }

      return resolveVerticalDropTarget({
        activeId: activeRowId,
        pointerY: dragCenterY,
        candidates: rowEntries,
      })
    },
    [getRowElement],
  )

  const updateRowDropTarget = (activeRowId: string, dragCenterY: number) => {
    // While an external drop target is hovered, the row indicator is cleared
    // and stays cleared (the scroll/drag-move paths also land here).
    if (externalDropHoverRef.current) {
      setRowDropTarget(null)
      return
    }

    const nextTarget = resolveRowDropTarget(activeRowId, dragCenterY)
    setRowDropTarget(nextTarget)

    if (nextTarget && onDragOver) {
      onDragOver(activeRowId, nextTarget.targetId, nextTarget.position)
    }
  }

  const handleDragMove = (event: DragMoveEvent) => {
    const activeRowId = String(event.active.id)

    // Prefer the real cursor Y captured by the pointermove listener so the drop
    // target matches where the user is actually pointing. Fall back to the
    // dragged element's translated rect (then start rect + delta) only if no
    // pointer sample is available yet.
    const startRect = rowDragStartRectRef.current
    const translatedRect = event.active.rect.current.translated

    let dragCenterY: number
    if (lastPointerYRef.current !== null) {
      dragCenterY = lastPointerYRef.current
    } else if (translatedRect) {
      dragCenterY = translatedRect.top + translatedRect.height / 2
    } else if (startRect) {
      dragCenterY = startRect.top + event.delta.y + startRect.height / 2
    } else {
      return
    }

    updateRowDropTarget(activeRowId, dragCenterY)
  }

  // Simplified drag end handler
  const handleDragEnd = (event: DragEndEvent) => {
    const activeRowId = String(event.active.id)
    const dropTarget = rowDropTarget
    const handledExternally = externalDropSession?.onDrop(activeRowId) === true

    setActiveId(null)
    setRowDropTarget(null)
    rowDragStartRectRef.current = null
    activeRowIdRef.current = null
    lastPointerYRef.current = null
    externalDropHoverRef.current = false
    rowDragListenerCleanupRef.current?.()
    rowDragListenerCleanupRef.current = null

    // Call parent drag end handler for cleanup
    if (onDragEnd) {
      onDragEnd()
    }

    if (handledExternally || !dropTarget || !onRowReorder) return

    if (activeRowId !== dropTarget.targetId) {
      onRowReorder(activeRowId, dropTarget.targetId, dropTarget.position)
    }
  }

  const handleRowDragCancel = () => {
    externalDropSession?.onCancel()
    setActiveId(null)
    setRowDropTarget(null)
    rowDragStartRectRef.current = null
    activeRowIdRef.current = null
    lastPointerYRef.current = null
    externalDropHoverRef.current = false
    rowDragListenerCleanupRef.current?.()
    rowDragListenerCleanupRef.current = null
    if (onDragEnd) {
      onDragEnd()
    }
  }

  // Handle internal expansion state changes and notify parent
  const handleInternalGroupExpansionChange = (
    newExpandedGroups: Set<string>,
  ) => {
    setCurrentExpandedGroups(newExpandedGroups)
    if (onGroupExpansionChange) {
      onGroupExpansionChange(newExpandedGroups)
    }
  }

  // Expand all groups
  const expandAllGroups = () => {
    if (!enableGroupExpansion) return

    // Extract all group IDs from the data
    const allGroupIds = new Set<string>()
    data.forEach(row => {
      const groupId = getGroupId ? getGroupId(row) : null
      if (groupId) {
        allGroupIds.add(groupId)
      }
    })

    handleInternalGroupExpansionChange(allGroupIds)
  }

  // Collapse all groups
  const collapseAllGroups = () => {
    if (!enableGroupExpansion) return

    handleInternalGroupExpansionChange(new Set())
  }

  // Toggle a single group's expansion state
  // Group whose rows just appeared via user expansion — its rows get a brief
  // enter animation. Cleared after the animation so scrolling/virtualization
  // remounts never animate.
  const [recentlyExpandedGroupId, setRecentlyExpandedGroupId] = useState<
    string | null
  >(null)
  useEffect(() => {
    if (recentlyExpandedGroupId === null) return
    const timeout = setTimeout(() => setRecentlyExpandedGroupId(null), 250)
    return () => clearTimeout(timeout)
  }, [recentlyExpandedGroupId])

  const toggleGroupExpansion = (groupId: string) => {
    if (!enableGroupExpansion) return

    const newExpandedGroups = new Set(currentExpandedGroups)
    if (newExpandedGroups.has(groupId)) {
      newExpandedGroups.delete(groupId)
    } else {
      newExpandedGroups.add(groupId)
      setRecentlyExpandedGroupId(groupId)
    }
    handleInternalGroupExpansionChange(newExpandedGroups)
  }

  // When column visibility state changes in props, update local state and table
  useEffect(() => {
    if (state?.columnVisibility && enableColumnVisibility) {
      const newVisibility = state.columnVisibility

      // Only update if actually different
      const current = JSON.stringify(columnVisibility)
      const incoming = JSON.stringify(newVisibility)

      if (current !== incoming) {
        setColumnVisibility(newVisibility)
      }
    }
  }, [state?.columnVisibility, enableColumnVisibility, columnVisibility])

  // Track the previous defaultExpandedGroups to detect external changes
  const prevDefaultExpandedGroupsRef = useRef<string | null>(null)

  // Sync expansion state when defaultExpandedGroups changes from external source
  useEffect(() => {
    // Create a stable string representation for comparison
    const newDefaultStr = JSON.stringify(
      Array.from(defaultExpandedGroups).sort(),
    )

    // Only update if the prop actually changed (not just a new Set instance with same content)
    if (prevDefaultExpandedGroupsRef.current !== newDefaultStr) {
      prevDefaultExpandedGroupsRef.current = newDefaultStr
      setCurrentExpandedGroups(new Set(defaultExpandedGroups))
    }
  }, [defaultExpandedGroups])

  // Initialize the table with proper row identification
  const table = useReactTable({
    data,
    columns: columnsWithCustomize,
    getRowId: row => getRowId(row), // Ensure proper row identification
    getCoreRowModel: getCoreRowModel(),
    ...(enableSorting && {
      ...(manualSorting
        ? { manualSorting: true }
        : { getSortedRowModel: getSortedRowModel() }),
      onSortingChange: updater => {
        const newSorting =
          typeof updater === 'function' ? updater(sorting) : updater
        setSorting(newSorting)

        // Call the external sorting handler if provided
        if (onSortingChange) {
          onSortingChange(newSorting)
        }
      },
      state: { sorting },
    }),
    ...(enableFiltering && {
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters,
      state: { columnFilters },
    }),
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility,
      state: { columnVisibility },
    }),
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      state: { rowSelection },
    }),
    initialState: {
      // Create initial column visibility state from columns
      columnVisibility: Object.fromEntries(
        columns.map(col => [col.id, col.hidden !== true]),
      ),
    },
    // Ensure column state gets updated correctly
    enableHiding: enableColumnVisibility,
  })

  const tableColumnLayout = useMemo(() => {
    const widths = table.getVisibleLeafColumns().map(column => {
      const meta = column.columnDef.meta as ColumnMeta | undefined
      const width = getColumnLayoutWidth(column.id, meta, columnWidths)
      return {
        columnId: column.id,
        width,
      }
    })

    return {
      widths,
      totalWidth: widths.reduce((total, column) => total + column.width, 0),
    }
  }, [columnWidths, table])

  const tableStyle = useMemo<CSSProperties | undefined>(() => {
    if (!enableColumnResizing || tableColumnLayout.totalWidth <= 0) {
      return undefined
    }

    return {
      tableLayout: 'fixed',
      width: `${tableColumnLayout.totalWidth}px`,
      minWidth: `${tableColumnLayout.totalWidth}px`,
    }
  }, [enableColumnResizing, tableColumnLayout.totalWidth])

  const cellGrid = useMemo<CellGrid>(() => {
    const rowIds = table
      .getRowModel()
      .rows.filter(row => {
        const rowOriginal = row.original as { type?: string }
        if (rowOriginal?.type === 'group') return false

        if (enableGroupExpansion && getGroupId) {
          const rowGroupId = getGroupId(row.original)
          if (!currentExpandedGroups.has(rowGroupId || '')) return false
        }

        return true
      })
      .map(row => getRowId(row.original))

    const columnIds = table.getVisibleLeafColumns().map(column => column.id)

    return { rowIds, columnIds }
  }, [currentExpandedGroups, enableGroupExpansion, getGroupId, getRowId, table])

  useEffect(() => {
    cellGridRef.current = cellGrid
  }, [cellGrid])

  // Rows that will actually render, after group-collapse filtering. Single
  // source of truth for both direct rendering and virtualization.
  const visibleTableRows = useMemo(() => {
    const rows = table.getRowModel().rows
    if (!enableGroupExpansion || !getGroupId) return rows
    return rows.filter(row => {
      const isGroupRow = (row.original as { type?: string })?.type === 'group'
      if (isGroupRow) return true
      return currentExpandedGroups.has(getGroupId(row.original) || '')
    })
    // `data` drives table.getRowModel().rows but the table instance is stable,
    // so it must be an explicit dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, data, currentExpandedGroups, enableGroupExpansion, getGroupId])

  // --- Row virtualization ---------------------------------------------------
  // Only rows near the viewport are mounted; spacer <tr>s preserve scroll
  // height. Requires the sticky-header wrapper to be the scroll container.
  // Rows attach the virtualizer's measurement ref, so expansion/custom rows
  // can have variable heights without disabling virtualization.
  const [virtualScrollElement, setVirtualScrollElement] =
    useState<HTMLElement | null>(null)
  const [virtualScrollMargin, setVirtualScrollMargin] = useState(0)

  const isVirtualized = enableVirtualization && virtualScrollElement !== null

  // Resolve the scroll wrapper, validate it actually scrolls, and measure the
  // tbody offset. Kept as a stable callback so it can be driven by BOTH row
  // composition changes AND viewport geometry changes — the safety valve and
  // scrollMargin are derived from live layout, so resolving on row count alone
  // is not enough (see the geometry effect below).
  const resolveVirtualization = useCallback(() => {
    if (!enableVirtualization) {
      setVirtualScrollElement(null)
      return
    }
    const wrapper = getScrollWrapper(containerRef.current)
    if (!wrapper || !containerRef.current) {
      setVirtualScrollElement(null)
      return
    }
    // Safety valve: if the wrapper isn't actually scrollable (e.g. the page
    // scrolls at the body level and the wrapper grows with content, or all
    // groups are collapsed so content fits the viewport), a virtualizer
    // attached to it would never receive scroll events and rows below the
    // first window would render as blank spacer. Fall back to full rendering
    // for now — this re-runs on row-composition AND geometry changes and
    // restores virtualization once the wrapper scrolls again.
    if (
      wrapper.clientHeight > 0 &&
      wrapper.scrollHeight <= wrapper.clientHeight + 1
    ) {
      setVirtualScrollElement(null)
      return
    }
    setVirtualScrollElement(wrapper)
    // Offset of the tbody within the scroll content (thead + anything above
    // the table), so multi-table scrollers stay roughly correct.
    const containerRect = containerRef.current.getBoundingClientRect()
    const scrollRect = wrapper.getBoundingClientRect()
    const theadHeight =
      containerRef.current.querySelector('thead')?.offsetHeight ?? 0
    const nextMargin =
      containerRect.top - scrollRect.top + wrapper.scrollTop + theadHeight
    // Guard the state write so the ResizeObserver below can't loop on a value
    // that hasn't actually moved.
    setVirtualScrollMargin(prev => (prev === nextMargin ? prev : nextMargin))
  }, [enableVirtualization])

  // Re-resolve when row composition changes (group expand/collapse, data
  // arrival) so the safety valve stays reversible.
  useEffect(() => {
    resolveVirtualization()
  }, [resolveVirtualization, visibleTableRows.length])

  // Re-resolve when the scroll viewport's geometry changes. scrollMargin and
  // the scrollability safety valve are computed from live layout, so a viewport
  // that finalizes or changes size AFTER the row-count-driven resolution
  // (flex/tab layout settling, window/panel resize, sidebar toggle) would
  // otherwise leave the virtualizer believing the viewport is only a few rows
  // tall — rendering just the overscan window over a large blank spacer with no
  // way to recover. Observing the wrapper (the ResizeObserver also delivers an
  // initial post-layout callback) closes that gap.
  useEffect(() => {
    if (!enableVirtualization) return
    const wrapper = getScrollWrapper(containerRef.current)
    if (!wrapper) return
    const handleGeometryChange = () => resolveVirtualization()
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(handleGeometryChange)
        : null
    observer?.observe(wrapper)
    window.addEventListener('resize', handleGeometryChange)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', handleGeometryChange)
    }
    // visibleTableRows.length is included so the observer re-binds to the
    // wrapper if it is (re)created when the table transitions to/from empty.
  }, [enableVirtualization, resolveVirtualization, visibleTableRows.length])

  const rowVirtualizer = useVirtualizer({
    count: isVirtualized ? visibleTableRows.length : 0,
    getScrollElement: () => virtualScrollElement,
    estimateSize: (index: number) => {
      const original = visibleTableRows[index]?.original as
        | { type?: string }
        | undefined
      return original?.type === 'group'
        ? GROUP_HEADER_ROW_HEIGHT
        : DEFAULT_ROW_HEIGHT
    },
    overscan: 12,
    scrollMargin: virtualScrollMargin,
  })

  // estimateSize closes over visibleTableRows; re-measure when the row
  // composition changes (e.g. group expand/collapse) so cached sizes stay valid.
  useEffect(() => {
    if (isVirtualized) {
      rowVirtualizer.measure()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVirtualized, visibleTableRows])

  const fillHandleCell = useMemo(() => {
    if (!selectedRange) return null

    const bounds = getCellRangeBounds(cellGrid, selectedRange)
    if (!bounds) return null

    return getCellAt(cellGrid, bounds.endRowIndex, bounds.endColumnIndex)
  }, [cellGrid, selectedRange])

  // Column drag handlers (after table is created)
  const handleColumnDragStart = (event: DragStartEvent) => {
    const columnId = String(event.active.id)
    setDraggedColumnId(columnId)
    setColumnDropIndicator(null)

    const headerElement = getColumnHeaderElement(columnId)
    if (!headerElement) {
      columnDragStartRectRef.current = null
      return
    }

    // Capture the pointer's starting X so the drop indicator can track the
    // cursor rather than the dragged column's center. Using the column center
    // makes drops land a spot or two away from where the user is pointing
    // whenever they grab a wide column off-center.
    const activator = event.activatorEvent as
      | { clientX?: number }
      | null
      | undefined
    const pointerStartX =
      typeof activator?.clientX === 'number' ? activator.clientX : null

    const rect = headerElement.getBoundingClientRect()
    columnDragStartRectRef.current = {
      left: rect.left,
      width: rect.width,
      pointerStartX,
    }
  }

  // Expose table instance to parent component
  useImperativeHandle(
    ref,
    () => ({
      table,
      expandAllGroups,
      collapseAllGroups,
      toggleGroupExpansion,
      resetColumnWidths,
    }),
    [
      table,
      expandAllGroups,
      collapseAllGroups,
      toggleGroupExpansion,
      resetColumnWidths,
    ],
  )

  // Get sortable row IDs for drag context
  const sortableRowIds = useMemo(
    () =>
      sortableRowIdsOverride ??
      data
        .filter(row => !isDraggableRow || isDraggableRow(row))
        .map(row => getRowId(row)),
    [data, getRowId, isDraggableRow, sortableRowIdsOverride],
  )

  // Get sortable column IDs for drag context
  const sortableColumnIds = table
    .getAllColumns()
    .filter(col => {
      const isFirstColumn = col.getIndex() === 0
      const meta = col.columnDef.meta as ColumnMeta | undefined
      return (
        !isFirstColumn &&
        !NON_REORDERABLE_COLUMN_IDS.has(col.id) &&
        !meta?.isFixed
      )
    })
    .map(col => col.id)

  const resolveColumnDropIndicator = useCallback(
    (
      activeColumnId: string,
      dragCenterX: number,
    ): ColumnDropIndicator | null => {
      const headerRects = sortableColumnIds
        .map(columnId => {
          const element = getColumnHeaderElement(columnId)
          if (!element) {
            return null
          }

          return {
            columnId,
            rect: element.getBoundingClientRect(),
          }
        })
        .filter(
          (entry): entry is { columnId: string; rect: DOMRect } =>
            entry !== null,
        )

      if (headerRects.length === 0) {
        return null
      }

      const containingHeader = headerRects.find(
        ({ rect }) => dragCenterX >= rect.left && dragCenterX <= rect.right,
      )

      const nearestHeader =
        containingHeader ||
        headerRects.reduce((closest, current) => {
          const currentDistance =
            dragCenterX < current.rect.left
              ? current.rect.left - dragCenterX
              : dragCenterX > current.rect.right
                ? dragCenterX - current.rect.right
                : 0
          const closestDistance =
            dragCenterX < closest.rect.left
              ? closest.rect.left - dragCenterX
              : dragCenterX > closest.rect.right
                ? dragCenterX - closest.rect.right
                : 0

          return currentDistance < closestDistance ? current : closest
        })

      if (!nearestHeader || nearestHeader.columnId === activeColumnId) {
        return null
      }

      const midpoint = nearestHeader.rect.left + nearestHeader.rect.width / 2

      return {
        targetColumnId: nearestHeader.columnId,
        position: dragCenterX < midpoint ? 'before' : 'after',
      }
    },
    [getColumnHeaderElement, sortableColumnIds],
  )

  const resetColumnDragState = () => {
    setDraggedColumnId(null)
    setColumnDropIndicator(null)
    columnDragStartRectRef.current = null
  }

  const handleColumnDragMove = (event: DragMoveEvent) => {
    const activeColumnId = String(event.active.id)
    const startRect = columnDragStartRectRef.current

    if (!startRect) {
      return
    }

    // Prefer the live cursor position (pointer start + drag delta) so the drop
    // indicator lands where the user is pointing. Fall back to the dragged
    // column's center only when the pointer position is unavailable.
    const dragCenterX =
      startRect.pointerStartX != null
        ? startRect.pointerStartX + event.delta.x
        : startRect.left + event.delta.x + startRect.width / 2
    setColumnDropIndicator(
      resolveColumnDropIndicator(activeColumnId, dragCenterX),
    )
  }

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const activeColumnId = String(event.active.id)
    const dropTarget = columnDropIndicator

    resetColumnDragState()

    if (!dropTarget || !onColumnReorder) {
      return
    }

    onColumnReorder(
      activeColumnId,
      dropTarget.targetColumnId,
      dropTarget.position,
    )
  }

  const handleColumnDragCancel = () => {
    resetColumnDragState()
  }

  // Define unified drag handlers (always defined, used conditionally)
  const hybridCollisionDetection: CollisionDetection = args => {
    const activeId = String(args.active.id)

    // Use appropriate collision detection based on what's being dragged
    if (sortableColumnIds.includes(activeId)) {
      // Column dragging - use pointerWithin for proper over state
      return pointerWithin(args)
    } else {
      // Row dragging - use custom collision detection
      return customCollisionDetection(args)
    }
  }

  const unifiedDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id)

    // Check if dragging a column (columns are in sortableColumnIds)
    if (sortableColumnIds.includes(activeId)) {
      handleColumnDragStart(event)
    } else {
      // Dragging a row
      handleDragStart(event)
    }
  }

  const unifiedDragMove = (event: DragMoveEvent) => {
    const activeId = String(event.active.id)

    if (sortableColumnIds.includes(activeId)) {
      handleColumnDragMove(event)
    } else {
      handleDragMove(event)
    }
  }

  const unifiedDragEnd = (event: DragEndEvent) => {
    const activeId = String(event.active.id)

    // Check if dragging a column
    if (sortableColumnIds.includes(activeId)) {
      handleColumnDragEnd(event)
    } else {
      // Dragging a row
      handleDragEnd(event)
    }
  }

  const unifiedDragCancel = () => {
    if (draggedColumnId) {
      handleColumnDragCancel()
      return
    }

    handleRowDragCancel()
  }

  // Render drag overlay content for the active item
  const columnDragOverlayContent = (() => {
    if (!draggedColumnId) {
      return null
    }

    const draggedColumn = table
      .getAllColumns()
      .find(column => column.id === draggedColumnId)
    const meta = draggedColumn?.columnDef.meta as ColumnMeta | undefined
    const label =
      meta?.label ||
      (typeof draggedColumn?.columnDef.header === 'string'
        ? draggedColumn.columnDef.header
        : draggedColumn?.id)

    if (!label) {
      return null
    }

    return (
      <div
        className="flex items-center rounded-md border border-primary/20 bg-background/95 px-3 py-2 text-xs font-medium uppercase tracking-[0.12em] text-foreground shadow-xl backdrop-blur-sm"
        style={
          columnDragStartRectRef.current?.width
            ? { width: columnDragStartRectRef.current.width }
            : undefined
        }
      >
        <span className="truncate">{label}</span>
      </div>
    )
  })()

  const rowDragOverlayContent =
    activeId && renderDragOverlay
      ? (() => {
          const activeItem = data.find(row => {
            const rowId = getRowId(row)
            const dragId = getDragId ? getDragId(row) : rowId
            return dragId === activeId
          })
          return activeItem ? renderDragOverlay(activeItem) : null
        })()
      : null

  const dragOverlayContent = columnDragOverlayContent || rowDragOverlayContent
  const dragOverlay =
    dragOverlayContent && typeof document !== 'undefined'
      ? createPortal(
          <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
            {dragOverlayContent}
          </DragOverlay>,
          document.body,
        )
      : null

  // Render table content with group headers outside table structure
  const renderRow = (
    row: (typeof visibleTableRows)[number],
    virtualIndex?: number,
  ): ReactNode => {
    const rowId = getRowId(row.original)
    const virtualRowProps =
      virtualIndex === undefined
        ? {}
        : {
            'data-index': virtualIndex,
            measureElement: rowVirtualizer.measureElement,
          }

    // Custom row renderer — lets consumers inject full-width rows (e.g. inline subtask panels)
    if (renderCustomRow) {
      const totalColumns = row.getVisibleCells().length
      const customContent = renderCustomRow(row.original, totalColumns)
      if (customContent !== null && customContent !== undefined) {
        return (
          <tr
            key={`custom-${rowId}`}
            data-drop-disabled="true"
            data-index={virtualIndex}
            ref={
              virtualIndex === undefined
                ? undefined
                : rowVirtualizer.measureElement
            }
            className="custom-expansion-row"
          >
            <td colSpan={totalColumns} className="border-0 p-0">
              {customContent}
            </td>
          </tr>
        )
      }
    }

    const isRowLoading = loadingRows.has(rowId)
    const isRowDraggable = isDraggableRow ? isDraggableRow(row.original) : false

    const isGroupHeaderRow =
      (row.original as { type?: string })?.type === 'group'

    if (isGroupHeaderRow) {
      // For group headers, we render individual cells like regular rows
      // but only populate the first cell with content to maintain sticky first column
      const visibleCells = row.getVisibleCells()
      const groupRowData = row.original as {
        groupId?: string
        __isExpanded?: boolean
        group?: { color?: string }
      }
      // Prefer the consumer's getGroupId — group rows don't all carry a
      // `groupId` property (e.g. My Work rows nest it as `group.id`), and an
      // empty id breaks header click-to-toggle + the expanded data attribute.
      const groupId =
        (getGroupId ? getGroupId(row.original) : null) ||
        groupRowData.groupId ||
        ''
      const groupColor = groupRowData.group?.color || ''
      const isExpanded = currentExpandedGroups.has(groupId)

      // Inject expansion state into row data for cell renderer to access
      groupRowData.__isExpanded = isExpanded

      const groupCells = visibleCells.map((cell, cellIndex) => {
        const isFirstCell = cellIndex === 0
        const cellContent = flexRender(
          cell.column.columnDef.cell,
          cell.getContext(),
        )

        return (
          <td
            key={cell.id}
            className={cn(
              'h-[52px] p-0',
              isFirstCell && 'group-header-first-cell',
            )}
            data-column={cell.column.id}
          >
            {cellContent}
          </td>
        )
      })

      const groupRowProps = {
        className: 'group-header-row',
        style: groupColor
          ? ({ '--group-color': groupColor } as React.CSSProperties)
          : undefined,
        onClick: () => {
          if (enableGroupExpansion && groupId) {
            toggleGroupExpansion(groupId)
          }
        },
        'data-expanded': isExpanded,
        'data-group-id': groupId,
      }

      const groupRowKey = `group-header-${rowId}`

      // A header renders as a sortable row when it's draggable itself OR when
      // the table opts into header drop targets (drop-on-header = insert at
      // the top of the group; also the only way into an empty group).
      if (enableRowReordering && (isRowDraggable || droppableGroupHeaders)) {
        const groupDragId = getDragId ? getDragId(row.original) : rowId
        return (
          <SortableGroupHeaderRow
            key={groupRowKey}
            sortableId={groupDragId}
            isDraggable={isRowDraggable}
            dropIndicator={dropIndicator}
            {...virtualRowProps}
            {...groupRowProps}
          >
            {groupCells}
          </SortableGroupHeaderRow>
        )
      }
      return (
        <UITableRow
          key={groupRowKey}
          data-index={virtualIndex}
          ref={
            virtualIndex === undefined
              ? undefined
              : rowVirtualizer.measureElement
          }
          {...groupRowProps}
        >
          {groupCells}
        </UITableRow>
      )
    }

    const RowComponent =
      enableRowReordering && isRowDraggable ? SortableTableRow : TableRow

    return (
      <RowComponent
        key={rowId}
        isDraggable={isRowDraggable}
        dragId={getDragId ? getDragId(row.original) : rowId}
        rowId={rowId}
        showPendingUpdateIndicator={showPendingUpdateIndicators}
        dropIndicator={dropIndicator}
        showDragHandle={showDragHandle}
        {...virtualRowProps}
        className={cn(
          typeof rowClassName === 'function'
            ? rowClassName(row.original)
            : rowClassName,
          isRowDraggable ? 'group/task-row' : '',
          isGroupHeaderRow ? 'group-header-row' : '',
          // Brief fade-in for rows revealed by a group expansion
          recentlyExpandedGroupId !== null &&
            getGroupId?.(row.original) === recentlyExpandedGroupId &&
            'group-row-enter',
        )}
        onClick={e => {
          if (onRowClick) {
            const target = e.target as HTMLElement
            if (
              target.closest(
                '[data-edit-trigger], [data-radix-popper-content-wrapper], button, [role="button"], [role="option"], input, select, textarea, [data-editing="true"]',
              )
            ) {
              return
            }
            onRowClick(row.original)
          }
        }}
      >
        {row.getVisibleCells().map(cell => {
          // Get the column definition to check for cell click handlers
          const columnDef = columns.find(col => col.id === cell.column.id)
          const cellPosition = { rowId, columnId: cell.column.id }
          const isActiveSelection = areSameCell(selectedCell, cellPosition)
          const isSelected =
            isActiveSelection ||
            isCellInRange(cellGrid, selectedRange, cellPosition)
          const cellKey = getCellKey(cellPosition)
          const showFillHandle =
            Boolean(enableCellSelection && isSelected) &&
            areSameCell(fillHandleCell, cellPosition)

          return (
            <TableCell
              key={cell.id}
              cell={cell}
              isRowLoading={isRowLoading}
              isDraggable={isRowDraggable}
              isDraggingTable={activeId !== null}
              isScrolling={false}
              isActivelyResizing={isActivelyResizing}
              columnWidth={columnWidths[cell.column.id]}
              rowId={rowId}
              isSelected={isSelected}
              isActiveSelection={isActiveSelection}
              isFillPreview={fillPreviewCellKeys.has(cellKey)}
              showFillHandle={showFillHandle}
              enableCellSelection={enableCellSelection}
              item={row.original}
              disableCellSelection={columnDef?.disableCellSelection}
              onCellClick={columnDef?.onCellClick}
              onCellDoubleClick={columnDef?.onCellDoubleClick}
              onCellSelect={handleCellSelect}
              onFillHandlePointerDown={handleFillHandlePointerDown}
            />
          )
        })}
      </RowComponent>
    )
  }

  const renderTableContent = () => {
    if (!isVirtualized) {
      return visibleTableRows.map(row => renderRow(row))
    }

    // Virtualized path: render only the rows near the viewport, with spacer
    // rows preserving total scroll height. Spacers are excluded from drop
    // targeting via data-drop-disabled.
    const virtualItems = rowVirtualizer.getVirtualItems()
    const totalSize = rowVirtualizer.getTotalSize()
    const paddingTop =
      (virtualItems[0]?.start ?? virtualScrollMargin) - virtualScrollMargin
    const paddingBottom =
      totalSize -
      ((virtualItems[virtualItems.length - 1]?.end ?? virtualScrollMargin) -
        virtualScrollMargin)

    const elements: ReactNode[] = []
    if (paddingTop > 0) {
      elements.push(
        <tr
          key="virtual-spacer-top"
          aria-hidden="true"
          data-drop-disabled="true"
          style={{ height: paddingTop }}
        />,
      )
    }
    for (const virtualItem of virtualItems) {
      const row = visibleTableRows[virtualItem.index]
      if (row) {
        elements.push(renderRow(row, virtualItem.index))
      }
    }
    if (paddingBottom > 0) {
      elements.push(
        <tr
          key="virtual-spacer-bottom"
          aria-hidden="true"
          data-drop-disabled="true"
          style={{ height: paddingBottom }}
        />,
      )
    }
    return elements
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className={cn(className, 'relative')}>
        <div className="relative">
          <div className="flex h-32 items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </div>
      </div>
    )
  }

  // Check if we have active column filters
  const hasActiveColumnFilters =
    internalColumnFilters && Object.keys(internalColumnFilters).length > 0

  // Default empty state for when no data exists
  const defaultEmptyState = (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted/60">
        <svg
          className="h-6 w-6 text-muted-foreground/70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-medium text-foreground">
        No results found
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Try adjusting your search or filter criteria
      </p>
    </div>
  )

  // Empty state content to show in table body
  const emptyStateContent = emptyState ?? defaultEmptyState

  // Simplified table element wrapped with TableContext
  const tableElement = (
    <TableContext value={tableContextValue}>
      <div
        className={cn(
          'data-table-container relative',
          showEmptyStateInBody && 'min-h-[400px]',
          className,
        )}
        ref={containerRef}
      >
        {/* Empty state overlay - pinned to the visible scroll viewport, not the full table width. */}
        {showEmptyStateInBody && (
          <div
            className="data-table-empty-state-overlay pointer-events-none absolute bottom-0 top-10 z-10 flex items-center justify-center"
            style={emptyStateViewportStyle}
          >
            <div className="pointer-events-auto">{emptyStateContent}</div>
          </div>
        )}
        <div className="data-table-scroll">
          <Table
            className={cn('data-table', draggedColumnId && 'column-dragging')}
            style={tableStyle}
          >
            {enableColumnResizing && tableColumnLayout.widths.length > 0 && (
              <colgroup>
                {tableColumnLayout.widths.map(column => (
                  <col
                    key={column.columnId}
                    data-column={column.columnId}
                    style={{
                      width: `${column.width}px`,
                      minWidth: `${column.width}px`,
                      maxWidth: `${column.width}px`,
                    }}
                  />
                ))}
              </colgroup>
            )}
            {!hideHeader && (
              <TableHeader>
                {enableColumnReordering ? (
                  <SortableContext
                    items={sortableColumnIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    {table.getHeaderGroups().map(headerGroup => (
                      <UITableRow key={`header-group-${headerGroup.depth}`}>
                        {headerGroup.headers.map(header => (
                          <TableHeaderCell
                            key={`${header.column.id}:${header.depth}`}
                            header={header}
                            enableColumnResizing={enableColumnResizing}
                            enableColumnReordering={enableColumnReordering}
                            onColumnResize={handleColumnResize}
                            onColumnResizeEnd={handleColumnResizeEnd}
                            columnWidth={columnWidths[header.column.id]}
                            dropIndicator={
                              columnDropIndicator?.targetColumnId ===
                              header.column.id
                                ? columnDropIndicator.position
                                : null
                            }
                            isColumnDragging={draggedColumnId !== null}
                            onHideColumn={onHideColumn}
                            onToggleSort={onToggleSort}
                            onEditField={onEditField}
                            onRenameColumn={onRenameColumn}
                            columnFilters={internalColumnFilters}
                            onColumnFilterChange={handleColumnFilterChange}
                            hideColumnMenu={hideColumnMenu}
                          />
                        ))}
                      </UITableRow>
                    ))}
                  </SortableContext>
                ) : (
                  table.getHeaderGroups().map(headerGroup => (
                    <UITableRow key={`header-group-${headerGroup.depth}`}>
                      {headerGroup.headers.map(header => (
                        <TableHeaderCell
                          key={`${header.column.id}:${header.depth}`}
                          header={header}
                          enableColumnResizing={enableColumnResizing}
                          enableColumnReordering={enableColumnReordering}
                          onColumnResize={handleColumnResize}
                          onColumnResizeEnd={handleColumnResizeEnd}
                          columnWidth={columnWidths[header.column.id]}
                          dropIndicator={null}
                          isColumnDragging={false}
                          onHideColumn={onHideColumn}
                          onToggleSort={onToggleSort}
                          onEditField={onEditField}
                          onRenameColumn={onRenameColumn}
                          columnFilters={internalColumnFilters}
                          onColumnFilterChange={handleColumnFilterChange}
                          hideColumnMenu={hideColumnMenu}
                        />
                      ))}
                    </UITableRow>
                  ))
                )}
              </TableHeader>
            )}
            <TableBody>
              {enableRowReordering ? (
                <SortableContext
                  items={sortableRowIds}
                  strategy={verticalListSortingStrategy}
                >
                  {showEmptyStateInBody ? (
                    <UITableRow className="empty-state-row border-b-0">
                      <td
                        colSpan={table.getAllColumns().length}
                        className="h-[350px] !bg-transparent p-0"
                      >
                        {/* Empty placeholder - actual content rendered as overlay */}
                      </td>
                    </UITableRow>
                  ) : (
                    renderTableContent()
                  )}
                </SortableContext>
              ) : showEmptyStateInBody ? (
                <UITableRow className="empty-state-row border-b-0">
                  <td
                    colSpan={table.getAllColumns().length}
                    className="h-[350px] !bg-transparent p-0"
                  >
                    {/* Empty placeholder - actual content rendered as overlay */}
                  </td>
                </UITableRow>
              ) : (
                renderTableContent()
              )}
              {/* Append row - always rendered as last row in table body */}
              {appendRow && (
                <UITableRow className="append-row border-t border-border/50 hover:bg-transparent">
                  <td colSpan={table.getAllColumns().length} className="p-0">
                    {appendRow}
                  </td>
                </UITableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </TableContext>
  )

  // Wrap with drag and drop context for column reordering and/or row reordering
  if (enableColumnReordering && enableRowReordering) {
    // Both enabled - use single context with hybrid collision detection
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={hybridCollisionDetection}
        onDragStart={unifiedDragStart}
        onDragMove={unifiedDragMove}
        onDragEnd={unifiedDragEnd}
        onDragCancel={unifiedDragCancel}
      >
        {tableElement}
        {dragOverlay}
      </DndContext>
    )
  }

  // Only column reordering enabled
  if (enableColumnReordering) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleColumnDragStart}
        onDragMove={handleColumnDragMove}
        onDragEnd={handleColumnDragEnd}
        onDragCancel={handleColumnDragCancel}
      >
        {tableElement}
        {dragOverlay}
      </DndContext>
    )
  }

  // Only row reordering enabled
  if (enableRowReordering) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={customCollisionDetection}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleRowDragCancel}
      >
        {tableElement}
        {dragOverlay}
      </DndContext>
    )
  }

  return tableElement
}

const ForwardedDataTable = forwardRef(DataTableWithRef)
ForwardedDataTable.displayName = 'DataTable'

const TypedDataTable = ForwardedDataTable as <T>(
  props: DataTableProps<T> & { ref?: ForwardedRef<DataTableRef<T>> },
) => ReactElement

export { TypedDataTable as DataTable }
